import {
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
} from "@coval/ui"
import { ChartFigure } from "@coval/ui/chart-figure"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { AXIS_TEXT_FILL, DIVIDER_STROKE } from "@/components/charts/chartTheme"
import { chartSeriesColors } from "@/components/charts/seriesPalette"

import type {
  GithubReceipts,
  GithubReceiptView,
} from "../../utils/githubReceiptSchemas"

const statusLabels = {
  queued: "Queued",
  running: "Running",
  publishing: "Publishing",
  succeeded: "Succeeded",
  failed: "Failed",
  needs_attention: "Needs attention",
} as const
const timeFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "Etc/GMT+8",
})
const dollars = (value: number | null) =>
  value === null
    ? "Unknown"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 4,
      }).format(value)

export function GithubReceiptsView({
  data,
  view,
  title,
  rolling = false,
}: {
  data: GithubReceipts
  view: GithubReceiptView
  title: string
  rolling?: boolean
}) {
  const totals = data.totals
  const stat =
    view === "job_count"
      ? {
          value: String(totals.jobs),
          detail: "Distinct jobs created in the selected range",
        }
      : view === "attention"
        ? {
            value: String(totals.needs_attention),
            detail: `${totals.failed} failed · ${totals.jobs} total jobs`,
          }
        : view === "published"
          ? {
              value: String(totals.published),
              detail:
                "Jobs with a recorded pull request; merge and recovery are not implied",
            }
          : view === "cost"
            ? {
                value: dollars(totals.cost_usd),
                detail: `Observed model cost from ${totals.cost_observed_jobs} of ${totals.jobs} jobs; excludes infrastructure and unreported usage`,
              }
            : view === "usage"
              ? {
                  value: `${totals.usage_receipts} / ${totals.jobs}`,
                  detail: `${totals.complete_usage} complete usage receipts · ${totals.cost_observed_jobs} with observed cost`,
                }
              : null
  const rows =
    view === "status"
      ? Object.entries(statusLabels).map(([key, name]) => ({
          name,
          value: totals[key as keyof typeof statusLabels],
        }))
      : view === "checks"
        ? ["passed", "failed", "timed_out", "skipped"].map((key) => ({
            name: key.replaceAll("_", " "),
            value: totals[`checks_${key}` as "checks_passed"],
          }))
        : view === "repositories"
          ? [...data.repositories]
              .sort(
                (a, b) =>
                  b.jobs - a.jobs || a.repository.localeCompare(b.repository),
              )
              .slice(0, 10)
              .map((row) => ({ name: row.repository, value: row.jobs }))
          : data.daily.map((row) => ({ name: row.date, value: row.jobs }))
  const summary =
    view === "checks"
      ? `${totals.evaluation_receipts} of ${totals.jobs} jobs have evaluation receipts. ${totals.check_pass_rate === null ? "No executed checks observed." : `${(totals.check_pass_rate * 100).toFixed(1)}% of executed checks passed; skipped checks excluded.`}`
      : view === "repositories"
        ? `${data.repositories.length} repositories; showing up to 10 by job count.`
        : view === "jobs_over_time"
          ? "Jobs by creation date, in PST day buckets. Latest receipt state is used."
          : "Latest receipt state for distinct jobs created in the selected range."
  const missingRowsMessage =
    totals.jobs === 0
      ? null
      : view === "jobs_over_time" && data.daily.length === 0
        ? "No daily breakdown was returned for this date range."
        : view === "repositories" && data.repositories.length === 0
          ? "No repository breakdown was returned for this date range."
          : view === "recent_jobs" && data.recent_jobs.length === 0
            ? "No recent job details were returned for this date range."
            : null
  return (
    <>
      <p className="text-xs text-coval-text-muted">
        All organizations · retained GitHub jobs ·{" "}
        {rolling ? "rolling range · " : ""}
        refreshes every minute
      </p>
      {(data.truncated ||
        data.invalid_receipts > 0 ||
        data.backfill_excluded > 0) && (
        <p role="status" className="text-sm text-coval-warning-text">
          Partial results:{" "}
          {data.truncated
            ? "10,000 receipt limit reached; narrow the date range. "
            : ""}
          {data.invalid_receipts + data.backfill_excluded} invalid or unindexed
          receipts excluded.
        </p>
      )}
      {missingRowsMessage ? (
        <p role="status" className="text-sm text-coval-text-muted">
          {missingRowsMessage}
        </p>
      ) : stat ? (
        <div className="flex flex-1 flex-col justify-center gap-2">
          <p className="text-4xl font-semibold tabular-nums text-coval-text">
            {stat.value}
          </p>
          <p className="text-sm text-coval-text-muted">{stat.detail}</p>
        </div>
      ) : view === "recent_jobs" ? (
        <ListTable aria-label={title}>
          <ListTableHeader>
            <ListTableRow>
              <ListTableHead>Job / repository</ListTableHead>
              <ListTableHead>Status</ListTableHead>
              <ListTableHead>Created (PST)</ListTableHead>
            </ListTableRow>
          </ListTableHeader>
          <ListTableBody>
            {data.recent_jobs.map((job) => (
              <ListTableRow
                key={`${job.organization_id}:${job.workspace_id}:${job.job_id}`}
              >
                <ListTableCell>
                  {job.pull_request_url ? (
                    <a
                      href={job.pull_request_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-coval-link underline"
                    >
                      {job.repository}
                    </a>
                  ) : (
                    (job.repository ?? "Unknown repository")
                  )}
                  <p className="text-xs text-coval-text-muted">{job.job_id}</p>
                </ListTableCell>
                <ListTableCell>{statusLabels[job.status]}</ListTableCell>
                <ListTableCell>
                  {timeFormat.format(new Date(job.created_at))}
                </ListTableCell>
              </ListTableRow>
            ))}
          </ListTableBody>
        </ListTable>
      ) : (
        <ChartFigure
          label={title}
          summary={summary}
          className="min-h-52 flex-1 shrink-0"
          table={{
            columns: [
              {
                key: "name",
                header: view === "jobs_over_time" ? "Created (PST)" : "Group",
              },
              {
                key: "value",
                header: view === "checks" ? "Checks" : "Jobs",
                numeric: true,
              },
            ],
            rows: rows.map((row) => ({ key: row.name, cells: row })),
          }}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={rows}
              margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
            >
              <CartesianGrid vertical={false} stroke={DIVIDER_STROKE} />
              <XAxis
                dataKey="name"
                tick={{ fill: AXIS_TEXT_FILL, fontSize: 11 }}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                width={35}
                tick={{ fill: AXIS_TEXT_FILL, fontSize: 11 }}
              />
              <Tooltip />
              <Bar
                dataKey="value"
                name={view === "checks" ? "Checks" : "Jobs"}
                fill={chartSeriesColors(1)[0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartFigure>
      )}
      {!stat && (
        <p className="text-xs text-coval-text-muted">
          {view === "recent_jobs"
            ? `Newest ${data.recent_jobs.length} of ${totals.jobs} jobs in range.`
            : summary}
        </p>
      )}
      {totals.jobs === 0 && (
        <p className="text-sm text-coval-text-muted">
          No retained jobs were created in this date range.
        </p>
      )}
      <p className="text-xs text-coval-text-muted">
        {rolling && (
          <>
            {timeFormat.format(new Date(data.start_date))} –{" "}
            {timeFormat.format(new Date(data.end_date))} PST ·{" "}
          </>
        )}
        Read {timeFormat.format(new Date(data.as_of))} PST · updates may take a
        moment to appear
      </p>
    </>
  )
}
